import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'mojito/utils';
import {
    useListItems,
    useEventGroups,
    useItemListLoadDone,
    useEventGroupsLoadDone,
} from 'modules/common/hooks';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import { buildCmsCouponLinks } from 'modules/cms-coupons-list/utils.js';
import { TOP_SPORTS_PRIMARY_TAB_ID, TOP_SPORTS_SECONDARY_TAB_ID } from './constants.js';

const { MenuRow } = MojitoPresentation.Components;
const { useScrollIntoElement, useSelectedItemScroll, useStickyState } = MojitoPresentation.Hooks;
const eventGroupsSelectors = MojitoServices.EventGroups.selectors;
const { selectEventGroupNames, selectState: selectEventGroupsState } = eventGroupsSelectors;

const STICKY_LAYOUT_STYLE_KEYS = ['top', 'zIndex', 'width'];

const pickStickyLayoutStyle = stickyStyle =>
    STICKY_LAYOUT_STYLE_KEYS.reduce((layoutStyle, key) => {
        if (stickyStyle[key] !== undefined) {
            layoutStyle[key] = stickyStyle[key];
        }
        return layoutStyle;
    }, {});

const isNonZeroLength = value =>
    typeof value === 'string' && value.trim() !== '' && parseFloat(value) !== 0;

const withPortalHeaderOffset = (value, portalHeaderHeight) => {
    if (!isNonZeroLength(portalHeaderHeight)) {
        return value;
    }
    if (!isNonZeroLength(value)) {
        return portalHeaderHeight;
    }
    return `calc(${value} + ${portalHeaderHeight})`;
};

const applyPortalHeaderOffset = (stickyStyle, portalHeaderHeight) => {
    if (!isNonZeroLength(portalHeaderHeight) || stickyStyle?.top === undefined) {
        return stickyStyle;
    }
    return { ...stickyStyle, top: withPortalHeaderOffset(stickyStyle.top, portalHeaderHeight) };
};

const pixelsToNumber = value => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const withPortalHeaderScrollOffset = (scrollOffset, portalHeaderHeight) => {
    if (!isNonZeroLength(portalHeaderHeight)) {
        return scrollOffset;
    }
    return `${pixelsToNumber(scrollOffset) + pixelsToNumber(portalHeaderHeight)}px`;
};

const STUCK_TOP_OFFSET_BUFFER_PX = 2;

const getStuckScrollThreshold = element => {
    const top = Number.parseFloat(window.getComputedStyle(element).top) || 0;

    return element.getBoundingClientRect().top + window.scrollY - top - STUCK_TOP_OFFSET_BUFFER_PX;
};

const useCouponMenuStickyState = (isEnabled, shouldRecalculate, stuckUsesFixedPosition) => {
    const [stickyRef, hasReachedStuckPosition] = useStickyState(isEnabled, shouldRecalculate);
    const [isStuck, setIsStuck] = useState(false);
    const stuckScrollThresholdRef = useRef(null);
    const hadReachedStuckPositionRef = useRef(false);

    useEffect(() => {
        if (!stuckUsesFixedPosition) {
            hadReachedStuckPositionRef.current = false;
            return;
        }

        const hasJustReachedStuckPosition =
            hasReachedStuckPosition && !hadReachedStuckPositionRef.current;

        hadReachedStuckPositionRef.current = hasReachedStuckPosition;

        if (!hasJustReachedStuckPosition || isStuck) {
            return;
        }

        const menuElement = stickyRef.current;

        if (!menuElement) {
            return;
        }

        stuckScrollThresholdRef.current = getStuckScrollThreshold(menuElement);
        setIsStuck(true);
    }, [hasReachedStuckPosition, isStuck, stuckUsesFixedPosition, stickyRef]);

    useEffect(() => {
        if (!stuckUsesFixedPosition || !isStuck) {
            return undefined;
        }

        const releaseStuckState = () => {
            const scrollThreshold = stuckScrollThresholdRef.current;

            if (scrollThreshold !== null && window.scrollY < scrollThreshold) {
                stuckScrollThresholdRef.current = null;
                setIsStuck(false);
            }
        };

        window.addEventListener('scroll', releaseStuckState, { passive: true });

        return () => window.removeEventListener('scroll', releaseStuckState);
    }, [isStuck, stuckUsesFixedPosition]);

    return [stickyRef, stuckUsesFixedPosition ? isStuck : hasReachedStuckPosition];
};

const CmsCouponMenuLinks = props => {
    const {
        mojitoTools: { config, instanceId, style },
        couponsListId,
        showPrimaryTopSportsTab = false,
        showSecondaryTopSportsTab = false,
        primaryTopSportsTabLabel = '',
        secondaryTopSportsTabLabel = '',
        onItemClick,
    } = props;
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [scrollTargetId, setScrollTargetId] = useState(null);
    const [scrollTrigger, setScrollTrigger] = useState(0);
    const groupIds = useListItems(couponsListId);
    const eventGroups = useEventGroups(groupIds, instanceId);
    const eventGroupNames = useSelector(state => selectEventGroupNames(state));
    const listLoadDone = useItemListLoadDone([couponsListId]);
    const eventGroupsLoadDone = useEventGroupsLoadDone(groupIds ?? []);
    const allCmsCouponsLoaded = listLoadDone && (isEmpty(groupIds) || eventGroupsLoadDone);
    const isStickyEnabled = style?.cmsCouponMenuContainer?.position === 'sticky';
    const stuckUsesFixedPosition = style?.cmsCouponMenuContainerSticky?.position === 'fixed';
    const [stickyRef, isSticky] = useCouponMenuStickyState(
        isStickyEnabled,
        allCmsCouponsLoaded,
        stuckUsesFixedPosition
    );
    const portalHeaderHeight = config.portalHeaderHeight;
    const couponMenuContainerStyle = useMemo(
        () =>
            applyPortalHeaderOffset(
                isSticky ? style.cmsCouponMenuContainerSticky : style.cmsCouponMenuContainer,
                portalHeaderHeight
            ),
        [
            isSticky,
            style.cmsCouponMenuContainer,
            style.cmsCouponMenuContainerSticky,
            portalHeaderHeight,
        ]
    );

    const scrollOffset = useMemo(
        () => withPortalHeaderScrollOffset(config.scrollOffset, portalHeaderHeight),
        [config.scrollOffset, portalHeaderHeight]
    );

    const liveLink = useMemo(
        () =>
            showPrimaryTopSportsTab
                ? {
                      id: TOP_SPORTS_PRIMARY_TAB_ID,
                      data: primaryTopSportsTabLabel,
                  }
                : undefined,
        [showPrimaryTopSportsTab, primaryTopSportsTabLabel]
    );

    const featuredLink = useMemo(
        () =>
            showSecondaryTopSportsTab
                ? {
                      id: TOP_SPORTS_SECONDARY_TAB_ID,
                      data: secondaryTopSportsTabLabel,
                  }
                : undefined,
        [showSecondaryTopSportsTab, secondaryTopSportsTabLabel]
    );

    const eventGroupTitles = useSelector(state => selectEventGroupsState(state).title || {});
    const cmsCouponLinks = useMemo(
        () => buildCmsCouponLinks(groupIds, eventGroups, eventGroupNames, eventGroupTitles),
        [groupIds, eventGroups, eventGroupNames, eventGroupTitles]
    );

    const menuItems = useMemo(
        () => [liveLink, ...cmsCouponLinks, featuredLink].filter(Boolean),
        [liveLink, cmsCouponLinks, featuredLink]
    );
    const scrollMenuItems = useMemo(
        () =>
            menuItems.map(({ id, data, className, icon, altText }) => ({
                id,
                data,
                className,
                icon,
                altText,
            })),
        [menuItems]
    );
    const isCmsCouponLinksReady = allCmsCouponsLoaded && cmsCouponLinks.length > 0;

    const orderedMenuItems = scrollMenuItems.map(couponLink => couponLink.id);
    const isNavigatingRef = useRef(false);
    const navigationTargetRef = useRef(null);

    useScrollIntoElement(scrollTargetId, isCmsCouponLinksReady, 0, 'smooth', scrollTrigger);

    useSelectedItemScroll(
        isCmsCouponLinksReady,
        orderedMenuItems,
        setSelectedItemId,
        scrollOffset,
        isNavigatingRef,
        navigationTargetRef,
        config.stickyExtraOffset
    );

    const handleItemClick = id => {
        onItemClick?.(id);
        setSelectedItemId(id);
        isNavigatingRef.current = true;
        navigationTargetRef.current = id;
        setScrollTargetId(id);
        setScrollTrigger(previousScrollTrigger => previousScrollTrigger + 1);
    };

    if (cmsCouponLinks.length === 0 || scrollMenuItems.length === 0) {
        return null;
    }

    return (
        <div ref={stickyRef} style={couponMenuContainerStyle} className="ta-CmsCouponMenuLinks">
            <MenuRow
                config={config.menuRow}
                menuItems={scrollMenuItems}
                selectedItemId={selectedItemId}
                cbItemClicked={handleItemClick}
            />
        </div>
    );
};

CmsCouponMenuLinks.getStyle = function (config, mode, merge) {
    const cmsCouponMenuContainer = merge(
        config.cmsCouponMenuContainer,
        config.cmsCouponMenuContainer.style
    );
    const cmsCouponMenuContainerSticky = merge(
        config.cmsCouponMenuContainerSticky,
        config.cmsCouponMenuContainerSticky.style
    );

    return {
        cmsCouponMenuContainer: merge(
            cmsCouponMenuContainer,
            pickStickyLayoutStyle(cmsCouponMenuContainerSticky)
        ),
        cmsCouponMenuContainerSticky: merge(cmsCouponMenuContainer, cmsCouponMenuContainerSticky),
    };
};

export default CmsCouponMenuLinks;
